import { useReadAloud } from '../hooks/useReadAloud';

/**
 * The "read this page aloud" control that sits at the top of each public form.
 *
 * Who it is for is worth being exact about, because it decides how it looks. Not blind candidates —
 * they arrive with a screen reader of their own and this would only talk over it. It is for someone
 * who can see the form perfectly well and finds reading it hard: a developmental or intellectual
 * disability, dyslexia, English as a second language, or a bad day. Edge has exactly this feature
 * built in and it is reached by a keyboard shortcut nobody is told about, which is no use at all to
 * a person who would have to read about it first.
 *
 * So: plain words, no icon on its own, and visible without being asked for. It renders nothing in a
 * browser with no speech synthesiser rather than offering a button that would do nothing.
 */
export function ReadAloud({ text }: { text: string }) {
  // Everything these forms say is written in English, and the voice has to be told so — see the note
  // in useReadAloud about what a machine set to another language does with it otherwise.
  const { supported, state, speak, pause, resume, stop } = useReadAloud('en-US');

  if (!supported || text.trim().length === 0) return null;

  return (
    <div className="read-aloud">
      {state === 'idle' ? (
        <button type="button" className="button button--quiet" onClick={() => speak(text)}>
          {/* Hidden from the reading: a screen reader says "speaker with three sound waves" for it,
              which is noise in front of a label that already says what the button does. */}
          <span className="read-aloud__icon" aria-hidden="true">
            🔊
          </span>
          Read this page aloud
        </button>
      ) : (
        <>
          <button
            type="button"
            className="button button--quiet"
            onClick={state === 'speaking' ? pause : resume}
          >
            {state === 'speaking' ? 'Pause reading' : 'Continue reading'}
          </button>
          <button type="button" className="button button--quiet" onClick={stop}>
            Stop reading
          </button>
        </>
      )}
    </div>
  );
}
